import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { billing } from "@/lib/billing";
import { useSubscription } from "@/hooks/useSubscription";
import { PlanSelector } from "@/components/PlanSelector";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Zap, CheckCircle2, Loader2, Settings, ArrowLeft } from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—";
const fmtPrice = (cents) => cents != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(cents / 100) : "";

export function ProSubscription() {
  const { data: sub, isLoading, isPro } = useSubscription();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [portalLoading, setPortalLoading] = useState(false);
  const polling = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (params.get("success") === "1" && sessionId && !polling.current) {
      polling.current = true;
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await billing.get(`/subscription/session-status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("WhooNow Pro attivo! Benvenuto.");
            qc.invalidateQueries({ queryKey: ["pro-subscription"] });
            setParams({}, { replace: true });
            return;
          }
        } catch { /* retry */ }
        if (attempts < 10) setTimeout(poll, 2000);
        else { toast.info("Attivazione in corso, aggiorna tra poco."); setParams({}, { replace: true }); }
      };
      poll();
    } else if (params.get("canceled") === "1") {
      toast.info("Checkout annullato. Nessun addebito effettuato.");
      setParams({}, { replace: true });
    }
  }, [params, setParams, qc]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await billing.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portale non disponibile");
      setPortalLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-6 sm:max-w-2xl sm:mx-auto" data-testid="subscription-page">
      <div className="space-y-1">
        <Link to="/pro" className="text-xs font-semibold text-volt flex items-center gap-1" data-testid="back-dashboard-link"><ArrowLeft className="w-3.5 h-3.5" /> Dashboard</Link>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Il mio abbonamento</h1>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 rounded-3xl" />
      ) : isPro ? (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-4" data-testid="subscription-active-card">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl bg-whoo text-white flex items-center justify-center"><Zap className="w-5 h-5" /></span>
            <div>
              <p className="font-heading font-bold text-slate-900 flex items-center gap-2">
                WhooNow Pro {sub.plan_label}
                <span className="text-[11px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="subscription-status-badge">Attivo</span>
              </p>
              <p className="text-sm text-slate-500">{fmtPrice(sub.amount_cents)}/{sub.interval}</p>
            </div>
          </div>
          <div className="text-sm text-slate-700 space-y-1">
            <p data-testid="subscription-renewal">
              {sub.cancel_at_period_end ? "Attivo fino al" : "Prossimo rinnovo"}: <span className="font-semibold">{fmtDate(sub.current_period_end)}</span>
            </p>
            {sub.cancel_at_period_end && (
              <p className="text-xs text-amber-600 font-semibold" data-testid="cancel-at-period-end-note">Cancellazione programmata: resterai Pro fino alla fine del periodo già pagato.</p>
            )}
            {sub.status === "past_due" && (
              <p className="text-xs text-red-600 font-semibold" data-testid="past-due-note">Ultimo pagamento non riuscito: aggiorna la carta dal portale per non perdere l'accesso Pro.</p>
            )}
          </div>
          <Button data-testid="manage-subscription-btn" onClick={openPortal} disabled={portalLoading} variant="outline" className="w-full h-12 rounded-full font-semibold">
            {portalLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Settings className="w-4 h-4 mr-2" /> Gestisci abbonamento</>}
          </Button>
          <p className="text-xs text-slate-400 text-center">Carta, fatture e cancellazione dal portale sicuro Stripe.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="subscription-free-view">
          <div className="bg-slate-900 rounded-3xl p-6 space-y-2">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-whoo" />
              <p className="font-heading font-bold text-white text-lg">Passa a WhooNow Pro</p>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              <span className="font-semibold text-white">0% commissioni sui tuoi lavori.</span> Un abbonamento semplice: quello che guadagni dai tuoi interventi rimane a te.
            </p>
          </div>
          <PlanSelector />
          <p className="text-xs text-slate-400 text-center">Pagamento sicuro con Stripe. Disdici quando vuoi: resti Pro fino alla fine del periodo pagato.</p>
        </div>
      )}
    </div>
  );
}
